import React, { useMemo, useState, useRef } from 'react';
import { Loader2, Plus, Pencil, Trash2, Search, Save, X, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { adminCreateLawyer, adminUpdateLawyer, adminDeleteLawyer, adminBulkUploadLawyers } from '../lib/api';

const CSV_TEMPLATE = 'name,city,specialty,experience,rating,languages,fee,email,phone,bio\n' +
  'Adv. Example Kumar,Chennai,Family & Matrimonial,8 yrs,4.7,Tamil|English,Free consult,example@example.com,9000000000,Short bio describing practice.\n';

const downloadTemplate = () => {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'lawyers-template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const EMPTY = {
  name: '', city: '', specialty: '', experience: '', rating: 4.5,
  languages: '', fee: 'Free consult', email: '', phone: '', bio: '',
};

const toPayload = (form) => ({
  ...form,
  rating: typeof form.rating === 'number' ? form.rating : parseFloat(form.rating) || 4.5,
  languages: typeof form.languages === 'string'
    ? form.languages.split(',').map((s) => s.trim()).filter(Boolean)
    : form.languages,
});

const fromLawyer = (l) => ({
  ...EMPTY,
  ...l,
  languages: Array.isArray(l.languages) ? l.languages.join(', ') : (l.languages || ''),
  rating: l.rating ?? 4.5,
});

const submitButtonLabel = (isSaving, isNew) => {
  if (isSaving) return 'Saving\u2026';
  if (isNew) return 'Add advocate';
  return 'Save changes';
};

const LawyersTab = ({ passcode, lawyers, onChange, onRefetch }) => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // lawyer object OR 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return lawyers;
    return lawyers.filter((l) =>
      [l.name, l.city, l.specialty, l.email, l.phone].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [lawyers, query]);

  const startNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };

  const startEdit = (l) => {
    setForm(fromLawyer(l));
    setEditing(l);
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setForm(EMPTY);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim() || !form.specialty.trim()) {
      toast.error('Name, city and specialty are required.');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await adminCreateLawyer(passcode, toPayload(form));
        toast.success('Lawyer added');
        onChange([created, ...lawyers]);
      } else {
        const updated = await adminUpdateLawyer(passcode, editing.id, toPayload(form));
        toast.success('Lawyer updated');
        onChange(lawyers.map((l) => l.id === editing.id ? updated : l));
      }
      setEditing(null);
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      await adminDeleteLawyer(passcode, confirmDelete.id);
      onChange(lawyers.filter((l) => l.id !== confirmDelete.id));
      toast.success(`${confirmDelete.name} removed`);
      setConfirmDelete(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not delete.');
    } finally {
      setDeletingId(null);
    }
  };

  const openBulk = () => {
    setBulkFile(null);
    setBulkResult(null);
    setBulkOpen(true);
  };

  const closeBulk = () => {
    if (bulkUploading) return;
    setBulkOpen(false);
    setBulkFile(null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error('Please choose a CSV file first.');
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await adminBulkUploadLawyers(passcode, bulkFile);
      setBulkResult(res);
      if (res.inserted_count > 0) {
        toast.success(`${res.inserted_count} advocate${res.inserted_count > 1 ? 's' : ''} added.`);
        if (typeof onRefetch === 'function') {
          onRefetch();
        }
      } else {
        toast('Nothing to add.', { description: 'No new rows were inserted.' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed. Please check the CSV format.');
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, city, specialty, email…"
            className="pl-10 h-11 border-[#E8DCC0] bg-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadTemplate}
            data-testid="download-csv-template-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#D6CAB0] text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4" /> CSV template
          </button>
          <button
            onClick={openBulk}
            data-testid="open-bulk-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#0F1B2D] text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors text-sm font-medium"
          >
            <Upload className="w-4 h-4" /> Bulk upload
          </button>
          <button
            onClick={startNew}
            className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Add lawyer
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E8DCC0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#EDE3CC] text-[#0F1B2D]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">City</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Specialty</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Contact</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, idx) => (
                <tr key={l.id} className={`border-t border-[#F0E7CE] ${idx % 2 ? 'bg-[#FCF8EE]' : 'bg-white'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#0F1B2D]">{l.name}</div>
                    <div className="text-xs text-[#5a5a5a]">{l.experience} · ★ {l.rating}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#3a3a3a]">{l.city}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#3a3a3a]">{l.specialty}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#3a3a3a]">
                    {l.email ? <div className="truncate max-w-[220px]">{l.email}</div> : <span className="text-[#9a9a9a]">—</span>}
                    {l.phone ? <div className="text-xs text-[#5a5a5a]">{l.phone}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => startEdit(l)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D6CAB0] text-[#0F1B2D] hover:bg-[#EDE3CC] transition-colors text-xs"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => setConfirmDelete(l)}
                        disabled={deletingId === l.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition-colors text-xs disabled:opacity-60"
                      >
                        {deletingId === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-[#5a5a5a]">No lawyers match.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="sm:max-w-2xl bg-[#F8F2E4] border border-[#E8DCC0] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-besley text-2xl text-[#0F1B2D]">
              {editing === 'new' ? 'Add advocate' : `Edit ${editing?.name || 'advocate'}`}
            </DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">
              Fields marked * are required. Languages should be comma-separated (e.g. &quot;Hindi, English&quot;).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0F1B2D]">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="Adv. Full Name" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">City *</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Specialty *</Label>
                <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="Family & Matrimonial" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Experience</Label>
                <Input value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="10 yrs" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Rating (0–5)</Label>
                <Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Fee</Label>
                <Input value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="Free consult" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#0F1B2D]">Languages (comma-separated)</Label>
                <Input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" placeholder="Hindi, English" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10 border-[#E8DCC0] bg-white" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#0F1B2D]">Bio</Label>
                <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 border-[#E8DCC0] bg-white" placeholder="Short paragraph describing practice areas and highlights." />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeEditor}
                disabled={saving}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-orange inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {submitButtonLabel(saving, editing === 'new')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent className="sm:max-w-2xl bg-[#F8F2E4] border border-[#E8DCC0] max-h-[85vh] overflow-y-auto" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-besley text-2xl text-[#0F1B2D]">Bulk upload advocates</DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">
              Upload a CSV to add many advocates at once. Uploaded advocates go live immediately (bypasses the ₹99 enrollment flow).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div className="rounded-lg border border-[#E8DCC0] bg-white p-4 text-sm">
              <p className="font-medium text-[#0F1B2D] mb-2">CSV format</p>
              <ul className="list-disc list-inside space-y-1 text-[#3a3a3a]">
                <li><b>Required columns:</b> name, city, specialty</li>
                <li><b>Optional columns:</b> experience, rating, languages, fee, email, phone, bio</li>
                <li>Separate multiple languages with a <b>pipe |</b> or <b>semicolon ;</b> (commas conflict with CSV).</li>
                <li>Max <b>500 rows</b> and <b>2 MB</b> per upload. Duplicates by name + city are auto-skipped.</li>
              </ul>
              <button
                type="button"
                onClick={downloadTemplate}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#A36B1F] hover:underline"
                data-testid="dialog-download-template-btn"
              >
                <Download className="w-3.5 h-3.5" /> Download sample template
              </button>
            </div>

            <div>
              <Label className="text-[#0F1B2D]">Choose CSV file</Label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  data-testid="bulk-csv-input"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#D6CAB0] text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" /> {bulkFile ? 'Change file' : 'Choose file'}
                </button>
                {bulkFile && (
                  <span className="text-sm text-[#3a3a3a] truncate max-w-xs" data-testid="bulk-selected-filename">
                    {bulkFile.name} · {(bulkFile.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            </div>

            {bulkResult && (
              <div className="rounded-lg border border-[#E8DCC0] bg-white p-4 text-sm" data-testid="bulk-upload-result">
                <div className="flex items-center gap-4 mb-3 text-[13px]">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {bulkResult.inserted_count} inserted
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-50 text-amber-900 border border-amber-200">
                    {bulkResult.skipped_count} skipped
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-800 border border-red-200">
                    <AlertCircle className="w-3.5 h-3.5" /> {bulkResult.error_count} errors
                  </span>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="mb-3">
                    <p className="font-medium text-[#0F1B2D] mb-1">Errors</p>
                    <ul className="text-xs text-red-800 space-y-0.5 max-h-32 overflow-y-auto">
                      {bulkResult.errors.map((e, i) => (
                        <li key={i}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {bulkResult.skipped?.length > 0 && (
                  <div>
                    <p className="font-medium text-[#0F1B2D] mb-1">Skipped (already existing)</p>
                    <ul className="text-xs text-[#5a5a5a] space-y-0.5 max-h-32 overflow-y-auto">
                      {bulkResult.skipped.map((s, i) => (
                        <li key={i}>Row {s.row}: {s.name} — {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {bulkResult.inserted_count > 0 && (
                  <p className="mt-3 text-xs text-[#5a5a5a]">
                    New advocates are already live on the public directory. Click <b>Refresh</b> on the dashboard to see full details.
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeBulk}
                disabled={bulkUploading}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60"
              >
                <X className="w-4 h-4" /> Close
              </button>
              <button
                type="button"
                data-testid="bulk-upload-submit-btn"
                onClick={handleBulkUpload}
                disabled={bulkUploading || !bulkFile}
                className="btn-orange inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {bulkUploading ? 'Uploading…' : 'Upload CSV'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the advocate from the public directory. Contact records already stored will remain untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LawyersTab;
